import { useState } from 'react'
import Layout from 'shared/layouts/containers/Main'
import useInit from './hooks/useInit'
import DeleteModal from 'shared/components/DeleteModal'
import SendEmailModal from 'components/SendEmailModal'
import { apiUrls, defaultPageSize, pageSizes, tableColumnWidths, roles, settings } from 'consts'
import getRows from './utils/getRows'
import {
  PagingState,
  SearchState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  SortingState,
  SelectionState,
  IntegratedSelection,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
  TableSelection,
  PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4'
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid/dist/dx-react-grid'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import hasAnyRole from 'shared/utils/hasAnyRole'
import getBrandContent from 'utils/getBrandContent'
import * as Icon from 'react-bootstrap-icons'
import { Button } from 'react-bootstrap'
import sendEmailReport from './utils/sendEmailReport'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import Footer from 'shared/components/Footer'
import showCustomerSwitcher from 'utils/showCustomerSwitcher'
import { IonIcon } from '@ionic/react'
import { cloudDownload, cloudUpload } from 'ionicons/icons'
import { NavLink } from 'react-router-dom'
import exportUsers from './utils/exportUsers'
import getPageSize from 'utils/getPageSize'
import unlockAccount from './utils/unlockAccount'

const Users: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [rows, setRows] = useState<any>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false)
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)

  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  const [selectedUsers, setSelectedUsers] = useState<{}>([])
  const [selectedUserIndexes, setSelectedUserIndexes] = useState<any>([])
  const [bulkActionsEnabled, setBulkActionsEnabled] = useState<boolean>(false)
  const [isSendEmailReportsRequestLoading, setIsSendEmailReportsRequestLoading] =
    useState<boolean>(false)
  const [isExportUsersRequestRunning, setIsExportUsersRequestRunning] = useState<boolean>(false)

  const isSuperAdmin: boolean = hasAnyRole([roles.superAdmin])
  const isAdmin: boolean = hasAnyRole([roles.admin, roles.superAdmin])

  const columns: { name: string; title: string; encodeHtml?: boolean; getCellValue?: any }[] = [
    { name: 'id', title: 'ID' },
    { name: 'concatened', title: 'Name' },
    { name: 'phone', title: 'Phone' },
    { name: 'email', title: 'Email' },
    { name: 'email_reports', title: 'Email Reports' },
    { name: 'role', title: 'Role' },
    { name: 'action', title: 'Actions', encodeHtml: false },
  ]

  const columnWidths: TableColumnWidthInfo[] = [
    { columnName: 'id', width: tableColumnWidths.id },
    { columnName: 'concatened', width: 'auto' },
    { columnName: 'phone', width: 'auto' },
    { columnName: 'email', width: 'auto' },
    { columnName: 'email_reports', width: 'auto' },
    { columnName: 'role', width: 'auto' },
    { columnName: 'action', width: tableColumnWidths.extended_action },
  ]

  useInit({
    getRows,
    setRows,
    setDeleteEntryId,
    setShowModal,
    setShowOverlay,
    unlockAccount,
    currentCustomer,
  })

  const handleBulkSelectionChanged = (selectedData) => {
    setSelectedUserIndexes(selectedData)
    setBulkActionsEnabled(selectedData.length > 0)
    setSelectedUsers(rows.filter((row, index) => selectedData.includes(index)))
  }

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className='with-action min-mobile-width' />
  )

  const SearchPanelInputComponent = (props) => {
    const insert: any = isSuperAdmin ? (
      <>
        <NavLink to='/import-users' className='btn btn-primary me-3'>
          <IonIcon
            icon={cloudUpload}
            className='ionicon'
            style={{ fontSize: '16px', marginBottom: '-3px' }}
          />
          Import Users
        </NavLink>
        <Button
          variant='success'
          onClick={(e) => {
            e.preventDefault()
            exportUsers({
              setIsExportUsersRequestRunning,
            })
          }}
          disabled={isExportUsersRequestRunning}
          className='btn btn-primary me-3'
        >
          {isExportUsersRequestRunning ? (
            <>
              <span
                className='spinner-border spinner-border-sm'
                role='status'
                aria-hidden='true'
              ></span>
              &nbsp;
            </>
          ) : (
            <IonIcon
              icon={cloudDownload}
              className='ionicon'
              style={{ fontSize: '16px', marginBottom: '-3px' }}
            />
          )}
          Export Users
        </Button>
      </>
    ) : undefined

    return SearchPanelInput({
      addUrl: isAdmin ? '/add-user' : undefined,
      addText: isAdmin ? 'Add User' : undefined,
      insert: insert,
      inputProps: props,
    })
  }

  const PagingContainer = (props) => {
    return <PagingPanel.Container {...props} className='table-footer' />
  }

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showMinimizedBarButton={true}
      showOverlay={showOverlay}
      headerInsert={
        showCustomerSwitcher() ? (
          <HeaderCustomerSelect
            setShowOverlay={setShowOverlay}
            handleCustomerChanged={(customer) => {
              setCurrentCustomer(customer)
              handleBulkSelectionChanged([])
            }}
          />
        ) : undefined
      }
    >
      <>
        <Grid rows={rows} columns={columns}>
          <SortingState defaultSorting={[{ columnName: 'email', direction: 'asc' }]} />
          <SearchState defaultValue='' />
          <IntegratedFiltering />
          <IntegratedSorting />
          <Toolbar />
          <SearchPanel inputComponent={SearchPanelInputComponent} />
          <SelectionState
            selection={selectedUserIndexes}
            onSelectionChange={(selectedData) => handleBulkSelectionChanged(selectedData)}
          />
          <PagingState
            defaultCurrentPage={0}
            defaultPageSize={getPageSize(settings.pageSizeUsers)}
          />
          <IntegratedPaging />
          {isSuperAdmin && <IntegratedSelection />}
          <Table tableComponent={TableComponent} />
          {isSuperAdmin && <TableSelection showSelectAll={true} />}
          <TableColumnResizing defaultColumnWidths={columnWidths} resizingMode='nextColumn' />
          <TableHeaderRow showSortingControls />
          <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />
        </Grid>
        {isSuperAdmin && (
          <>
            <div className='bottom-buttons'>
              <Button
                disabled={!bulkActionsEnabled || isSendEmailReportsRequestLoading}
                onClick={(e) =>
                  sendEmailReport({
                    selectedUsers,
                    setShowOverlay,
                    setBulkActionsEnabled,
                    setIsSendEmailReportsRequestLoading,
                  })
                }
                variant='primary'
                size='sm'
                className='float-right'
              >
                {isSendEmailReportsRequestLoading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    &nbsp;
                  </>
                ) : (
                  <Icon.BarChart />
                )}
                &nbsp; Send Email Reports
              </Button>
              <Button
                disabled={!bulkActionsEnabled}
                onClick={(e) => setShowEmailModal(true)}
                variant='primary'
                size='sm'
                className='float-right btn-margin'
              >
                <Icon.Envelope />
                &nbsp; Send Email Message
              </Button>
            </div>
            <SendEmailModal
              selectedUsers={selectedUsers}
              showModal={showEmailModal}
              setShowModal={setShowEmailModal}
            />
          </>
        )}
        {isAdmin && (
          <DeleteModal
            url={apiUrls.users}
            showModal={showModal}
            setShowModal={setShowModal}
            deleteEntryId={deleteEntryId}
            setDeleteEntryId={setDeleteEntryId}
            entityName='User'
            getRows={getRows}
            setRows={setRows}
          />
        )}
      </>
    </Layout>
  )
}

export default Users
